/**
 * Client-side "Generate Python" codegen for the Agent Builder Build canvas.
 *
 * Ported from Agent Studio's platform/codegen.ts and adapted to BTS:
 *  - types come from ../../types/agentBuilder (not the studio platform/types),
 *  - the guardrail catalog is the API-loaded GuardrailDef[] (gr-injection / gr-pii /
 *    gr-schema / gr-groundedness / gr-action) instead of a hard-coded seed,
 *  - unknown guardrail ids are skipped gracefully.
 *
 * Turns a WorkflowGraph into runnable, framework-FREE Python — no LangChain /
 * LangGraph. Pure asyncio + a tiny base-agent/guardrail kernel.
 */

import type { GeneratedFile, GuardrailDef, WorkflowGraph, WorkflowNode } from '../../types/agentBuilder';
import { TIER_MODEL } from './llmTiers';

const pyId = (s: string) =>
  s
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, '_$1')
    .toLowerCase();

const className = (s: string) =>
  s
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join('') || 'Agent';

// ---- guardrail id -> generated python function name ------------------------
// Mirrors the BTS built-in guardrail catalog (src/api/routes/agents.py).
const GUARD_FN: Record<string, string> = {
  'gr-injection': 'prompt_injection_scan',
  'gr-pii': 'pii_scan',
  'gr-schema': 'schema_check',
  'gr-groundedness': 'groundedness_check',
  'gr-action': 'action_policy',
};

function guardFn(id: string): string {
  return GUARD_FN[id] ?? '';
}

// ---- static kernel: identical across every generated project --------------

const KERNEL = `"""Framework-free agent kernel (NO LangChain / LangGraph).

A deliberately small, dependency-light core that every Amplinth agentic
workflow is generated against. Pure asyncio; LLM access goes through a single
provider abstraction with severity-based tier routing.
"""
from __future__ import annotations

import asyncio
import time
from abc import ABC, abstractmethod
from dataclasses import dataclass, field
from enum import Enum
from typing import Any, Awaitable, Callable, Dict, List, Optional


class AgentStatus(str, Enum):
    PENDING = "pending"
    RUNNING = "running"
    SUCCESS = "success"
    PARTIAL = "partial"
    FAILED = "failed"
    SKIPPED = "skipped"
    AWAITING_HITL = "awaiting_hitl"


class LlmTier(str, Enum):
    PREMIUM = "premium"
    MID = "mid"
    COST = "cost"


@dataclass
class GuardrailVerdict:
    name: str
    kind: str               # input | output | action
    passed: bool
    severity: str           # block | warn
    detail: str = ""


@dataclass
class AgentResult:
    """Uniform per-agent result used across the platform."""
    agent_name: str
    status: AgentStatus
    data: Dict[str, Any] = field(default_factory=dict)
    confidence: float = 0.0          # 0..1
    reasoning: str = ""
    tokens: int = 0
    cost_usd: float = 0.0
    duration_seconds: float = 0.0
    errors: List[str] = field(default_factory=list)
    guardrails: List[GuardrailVerdict] = field(default_factory=list)


@dataclass
class AgentContext:
    """Shared state threaded through every agent (immutable inputs, enriched
    outputs)."""
    task: str
    tenant_id: Optional[str] = None
    state: Dict[str, Any] = field(default_factory=dict)
    results: Dict[str, AgentResult] = field(default_factory=dict)

    def put(self, key: str, value: Any) -> None:
        self.state[key] = value

    def get(self, key: str, default: Any = None) -> Any:
        return self.state.get(key, default)


# --------------------------------------------------------------------------- #
# LLM provider abstraction — tier routing by severity. Never call a vendor SDK
# from agent code; always go through this.
# --------------------------------------------------------------------------- #

TIER_MODELS = {
    LlmTier.PREMIUM: "${TIER_MODEL.premium}",
    LlmTier.MID: "${TIER_MODEL.mid}",
    LlmTier.COST: "${TIER_MODEL.cost}",
}


class LlmProvider:
    async def complete(self, *, tier: LlmTier, system: str, prompt: str) -> Dict[str, Any]:
        """Return {text, tokens, cost_usd}. Wire this to your real provider."""
        model = TIER_MODELS[tier]
        # TODO: replace with your Anthropic / OpenAI / Azure client call.
        raise NotImplementedError(f"Wire LlmProvider.complete -> {model}")


# --------------------------------------------------------------------------- #
# Guardrails — input / output / action pipelines.
# --------------------------------------------------------------------------- #

Guardrail = Callable[[AgentContext, str], Awaitable[GuardrailVerdict]]


class GuardrailPipeline:
    def __init__(self, guardrails: List[Guardrail]):
        self._guardrails = guardrails

    async def run(self, ctx: AgentContext, payload: str) -> List[GuardrailVerdict]:
        return [await g(ctx, payload) for g in self._guardrails]

    @staticmethod
    def blocked(verdicts: List[GuardrailVerdict]) -> bool:
        return any(v.severity == "block" and not v.passed for v in verdicts)


# --------------------------------------------------------------------------- #
# Base agent — timeout-protected, guardrailed, returns AgentResult.
# --------------------------------------------------------------------------- #

class BaseAgent(ABC):
    name: str = "agent"
    tier: LlmTier = LlmTier.MID
    timeout_seconds: float = 60.0

    def __init__(self, llm: LlmProvider,
                 input_guardrails: Optional[GuardrailPipeline] = None,
                 output_guardrails: Optional[GuardrailPipeline] = None):
        self.llm = llm
        self.input_guardrails = input_guardrails or GuardrailPipeline([])
        self.output_guardrails = output_guardrails or GuardrailPipeline([])

    @abstractmethod
    async def run(self, ctx: AgentContext) -> AgentResult:
        ...

    async def execute(self, ctx: AgentContext) -> AgentResult:
        started = time.perf_counter()
        try:
            result = await asyncio.wait_for(self.run(ctx), timeout=self.timeout_seconds)
        except asyncio.TimeoutError:
            return AgentResult(agent_name=self.name, status=AgentStatus.FAILED,
                               errors=["timeout"],
                               duration_seconds=time.perf_counter() - started)
        except Exception as exc:  # noqa: BLE001
            return AgentResult(agent_name=self.name, status=AgentStatus.FAILED,
                               errors=[str(exc)],
                               duration_seconds=time.perf_counter() - started)
        result.duration_seconds = time.perf_counter() - started
        ctx.results[self.name] = result
        return result
`;

// ---- concrete guardrail function bodies (only the used ones are emitted) ----

const GUARD_BODY: Record<string, string> = {
  prompt_injection_scan: `async def prompt_injection_scan(ctx: AgentContext, payload: str) -> GuardrailVerdict:
    lowered = payload.lower()
    markers = ("ignore previous", "disregard the", "override your", "system prompt")
    hit = any(m in lowered for m in markers)
    return GuardrailVerdict("Prompt Injection", "input", not hit, "block",
                            "no injection markers" if not hit else "possible prompt-injection")`,
  pii_scan: `async def pii_scan(ctx: AgentContext, payload: str) -> GuardrailVerdict:
    hit = bool(_PII.search(payload))
    return GuardrailVerdict("PII Scan", "output", not hit, "block",
                            "no PII found" if not hit else "PII detected in output")`,
  schema_check: `async def schema_check(ctx: AgentContext, payload: str) -> GuardrailVerdict:
    ok = bool(payload and payload.strip())
    return GuardrailVerdict("Schema", "output", ok, "block",
                            "output non-empty" if ok else "empty / malformed output")`,
  groundedness_check: `async def groundedness_check(ctx: AgentContext, payload: str) -> GuardrailVerdict:
    # Replace with real claim<->context entailment scoring.
    grounded = bool(ctx.get("retrieved_context"))
    return GuardrailVerdict("Groundedness", "output", grounded, "warn",
                            "claims traceable to context" if grounded else "ungrounded claim")`,
  action_policy: `async def action_policy(ctx: AgentContext, payload: str) -> GuardrailVerdict:
    conf = float(ctx.get("confidence", 0.0))
    ok = conf >= 0.80
    return GuardrailVerdict("Action Policy", "action", ok, "block",
                            f"confidence {conf:.2f} {'>=' if ok else '<'} 0.80 -> "
                            f"{'auto-resolve' if ok else 'escalate to HITL'}")`,
};

function guardrailKernel(usedFns: string[]): string {
  const needsPii = usedFns.includes('pii_scan');
  const bodies = usedFns.map((fn) => GUARD_BODY[fn]).filter(Boolean);
  if (bodies.length === 0) {
    return `"""No guardrails configured for this workflow."""\n`;
  }
  return `"""Concrete guardrails generated from the Studio configuration."""
from __future__ import annotations

import re

from kernel import AgentContext, GuardrailVerdict
${needsPii ? '\n_PII = re.compile(r"[\\w.+-]+@[\\w-]+\\.[\\w.-]+|\\b\\d{12,19}\\b")\n' : ''}

${bodies.join('\n\n\n')}
`;
}

function agentStub(node: WorkflowNode): string {
  const cls = className(node.label);
  const tier = (node.llmTier ?? 'mid').toUpperCase();
  const objective = (node.objective ?? `Fulfil the ${node.role} objective for "${node.label}".`)
    .replace(/"""/g, '\\"\\"\\"');
  return `class ${cls}(BaseAgent):
    name = "${node.label}"
    tier = LlmTier.${tier}

    async def run(self, ctx: AgentContext) -> AgentResult:
        objective = """${objective}"""
        # Input guardrails (e.g. prompt-injection / cost ceiling)
        await self.input_guardrails.run(ctx, ctx.task)

        completion = await self.llm.complete(
            tier=self.tier, system=objective, prompt=str(ctx.state),
        )
        output = completion["text"]

        # Output / action guardrails
        verdicts = await self.output_guardrails.run(ctx, output)
        status = AgentStatus.SUCCESS
        if GuardrailPipeline.blocked(verdicts):
            status = AgentStatus.AWAITING_HITL  # escalate instead of emitting

        result = AgentResult(
            agent_name=self.name, status=status, data={"output": output},
            confidence=0.0,  # TODO: derive from the model / self-eval
            reasoning="${node.role} step complete",
            tokens=completion.get("tokens", 0), cost_usd=completion.get("cost_usd", 0.0),
            guardrails=verdicts,
        )
        ctx.put(${JSON.stringify(pyId(node.label) + '_output')}, output)
        return result
`;
}

function orchestrator(graph: WorkflowGraph, usedFns: string[]): string {
  const agentNodes = graph.nodes.filter((n) => n.kind !== 'output');
  const instances = agentNodes
    .map(
      (n) =>
        `        self.${pyId(n.label)} = ${className(n.label)}(llm, output_guardrails=GuardrailPipeline([${(n.guardrails ?? [])
          .map((g) => guardFn(g))
          .filter(Boolean)
          .join(', ')}]))`,
    )
    .join('\n');

  // Build adjacency for sequencing description.
  const order = topoOrder(graph);
  let body = '';

  if (graph.strategy === 'sequential') {
    body = order
      .map((n) => `        await self.${pyId(n.label)}.execute(ctx)   # ${n.role}`)
      .join('\n');
  } else if (graph.strategy === 'supervisor-routing') {
    const sup = agentNodes.find((n) => n.kind === 'orchestrator') ?? agentNodes[0];
    const specialists = agentNodes.filter((n) => n.role === 'specialist');
    const post = agentNodes.filter(
      (n) => n.role === 'critic' || n.kind === 'guardrail' || n.role === 'validator',
    );
    body =
      `        # 1) supervisor classifies + routes\n` +
      `        await self.${pyId(sup.label)}.execute(ctx)\n` +
      `        route = ctx.get("route")\n` +
      `        # 2) dispatch to the routed specialist\n` +
      `        specialists = {\n` +
      specialists
        .map((n) => `            ${JSON.stringify(routeKey(n.label))}: self.${pyId(n.label)},`)
        .join('\n') +
      `\n        }\n` +
      `        agent = specialists.get(route) or next(iter(specialists.values()))\n` +
      `        await agent.execute(ctx)\n` +
      post.map((n) => `        await self.${pyId(n.label)}.execute(ctx)   # ${n.role}`).join('\n');
  } else {
    // dispatch-waves: group by topological depth, gather each wave.
    const loopOnly = refineLoopOnlyIds(graph);
    let waveNo = 0;
    const waves = topoWaves(graph)
      .map((w) => w.filter((n) => n.kind !== 'output' && !loopOnly.has(n.id)))
      .filter((w) => w.length > 0);
    body = waves
      .map((runnable) => {
        waveNo += 1;
        if (runnable.length === 1)
          return `        await self.${pyId(runnable[0].label)}.execute(ctx)   # wave ${waveNo}`;
        return (
          `        # wave ${waveNo} — independent, run concurrently\n` +
          `        await asyncio.gather(\n` +
          runnable.map((n) => `            self.${pyId(n.label)}.execute(ctx),`).join('\n') +
          `\n        )`
        );
      })
      .join('\n');
  }

  // Validator refine loops.
  const validators = graph.nodes.filter((n) => n.refineTo);
  const refineBlock = validators
    .map((v) => {
      const target = graph.nodes.find((n) => n.id === v.refineTo);
      if (!target) return '';
      return `        # refine loop: re-run ${target.label} until quality passes (max ${v.maxIterations ?? 3})
        for _ in range(${v.maxIterations ?? 3}):
            vr = ctx.results.get("${v.label}")
            if not vr or vr.confidence >= 0.70:
                break
            await self.${pyId(target.label)}.execute(ctx)
            await self.${pyId(v.label)}.execute(ctx)`;
    })
    .filter(Boolean)
    .join('\n');

  const guardImport = usedFns.length
    ? `from guardrails import ${usedFns.join(', ')}\n`
    : '';

  return `"""Generated orchestrator for: ${graph.name}
Strategy: ${graph.strategy} — framework-free asyncio.
"""
from __future__ import annotations

import asyncio
from typing import Any, Dict

from kernel import (AgentContext, AgentResult, AgentStatus, BaseAgent,
                    GuardrailPipeline, LlmProvider, LlmTier)
from agents import (${agentNodes.map((n) => className(n.label)).join(', ')})
${guardImport}

class ${className(graph.name)}Orchestrator:
    """${graph.description ?? ''}"""

    def __init__(self, llm: LlmProvider):
${instances || '        pass'}

    async def run(self, task: str, tenant_id: str | None = None) -> Dict[str, Any]:
        ctx = AgentContext(task=task, tenant_id=tenant_id)
${body || '        pass'}
${refineBlock ? '\n' + refineBlock + '\n' : ''}
        return self._assemble(ctx)

    def _assemble(self, ctx: AgentContext) -> Dict[str, Any]:
        results = list(ctx.results.values())
        confidences = [r.confidence for r in results if r.status == AgentStatus.SUCCESS]
        return {
            "task": ctx.task,
            "status": "success" if all(r.status != AgentStatus.FAILED for r in results) else "partial",
            "overall_confidence": (sum(confidences) / len(confidences)) if confidences else 0.0,
            "total_tokens": sum(r.tokens for r in results),
            "total_cost_usd": round(sum(r.cost_usd for r in results), 4),
            "agents": {r.agent_name: r.data for r in results},
            "state": ctx.state,
        }


async def main() -> None:
    # llm = YourLlmProvider()   # implement kernel.LlmProvider
    # orch = ${className(graph.name)}Orchestrator(llm)
    # print(await orch.run("<your task here>"))
    ...


if __name__ == "__main__":
    asyncio.run(main())
`;
}

const routeKey = (label: string) => label.toLowerCase().replace(/\s*specialist\s*/, '').trim();

/** Nodes that exist only to service a validator's refine loop — i.e. they are a
 * refineTo target and have no normal (non-feedback) inbound edge. They must not
 * appear in a forward execution wave. */
function refineLoopOnlyIds(graph: WorkflowGraph): Set<string> {
  const targets = new Set(graph.nodes.map((n) => n.refineTo).filter(Boolean) as string[]);
  const out = new Set<string>();
  for (const id of targets) {
    const hasForwardParent = graph.edges.some((e) => e.target === id && !e.feedback);
    if (!hasForwardParent) out.add(id);
  }
  return out;
}

function topoOrder(graph: WorkflowGraph): WorkflowNode[] {
  const nodes = graph.nodes.filter((n) => n.kind !== 'output');
  const indeg = new Map(nodes.map((n) => [n.id, 0]));
  for (const e of graph.edges) {
    if (e.feedback) continue;
    if (indeg.has(e.target)) indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1);
  }
  const out: WorkflowNode[] = [];
  const seen = new Set<string>();
  let frontier = nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0);
  while (frontier.length) {
    for (const n of frontier) {
      if (seen.has(n.id)) continue;
      seen.add(n.id);
      out.push(n);
    }
    const next: WorkflowNode[] = [];
    for (const e of graph.edges) {
      if (e.feedback) continue;
      if (seen.has(e.source) && !seen.has(e.target)) {
        const t = nodes.find((n) => n.id === e.target);
        if (t) next.push(t);
      }
    }
    frontier = next;
  }
  for (const n of nodes) if (!seen.has(n.id)) out.push(n);
  return out;
}

function topoWaves(graph: WorkflowGraph): WorkflowNode[][] {
  const depth = new Map<string, number>();
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const compute = (id: string, guard = new Set<string>()): number => {
    if (depth.has(id)) return depth.get(id)!;
    if (guard.has(id)) return 0;
    guard.add(id);
    const parents = graph.edges.filter((e) => e.target === id && !e.feedback).map((e) => e.source);
    const d = parents.length ? Math.max(...parents.map((p) => compute(p, guard) + 1)) : 0;
    depth.set(id, d);
    return d;
  };
  graph.nodes.forEach((n) => compute(n.id));
  const maxD = Math.max(0, ...[...depth.values()]);
  const waves: WorkflowNode[][] = [];
  for (let d = 0; d <= maxD; d++) {
    const w = graph.nodes.filter((n) => depth.get(n.id) === d).map((n) => byId.get(n.id)!);
    if (w.length) waves.push(w);
  }
  return waves;
}

/**
 * Generate the framework-free Python project for a workflow graph.
 * @param graph the authoring WorkflowGraph
 * @param guardrails the API-loaded guardrail catalog (for README names); optional.
 */
export function generateCode(graph: WorkflowGraph, guardrails: GuardrailDef[] = []): GeneratedFile[] {
  const agentNodes = graph.nodes.filter((n) => n.kind !== 'output');
  const usedGuards = new Set<string>();
  agentNodes.forEach((n) => (n.guardrails ?? []).forEach((g) => usedGuards.add(g)));
  // Preserve a stable function ordering for the import line.
  const usedFns = Object.keys(GUARD_FN)
    .filter((id) => usedGuards.has(id))
    .map((id) => GUARD_FN[id]);

  const guardName = (id: string) =>
    guardrails.find((x) => x.guardrailId === id)?.name ?? id;

  const agentsFile =
    `"""Generated agents for: ${graph.name}.\n\nEach agent extends the framework-free BaseAgent kernel."""\n` +
    `from __future__ import annotations\n\n` +
    `from kernel import (AgentContext, AgentResult, AgentStatus, BaseAgent,\n` +
    `                    GuardrailPipeline, LlmTier)\n\n\n` +
    (agentNodes.map(agentStub).join('\n\n') || '# (no agents configured yet)\n');

  const requirements = `# Framework-free — only the standard library + your LLM SDK.\n# Add ONE of these for kernel.LlmProvider:\n# anthropic>=0.40\n# openai>=1.50\npydantic>=2.7\n`;

  const readme = `# ${graph.name} — generated agentic workflow

Strategy: **${graph.strategy}** · Product: **${graph.product ?? ''}**

${graph.description ?? ''}

Generated by **Amplinth Agent Builder**. Pure asyncio — **no LangChain / LangGraph**.

## Files
- \`kernel.py\`      — base agent, guardrail pipeline, LLM tier routing
- \`guardrails.py\`  — concrete guardrails (${[...usedGuards].map(guardName).join(', ') || 'none'})
- \`agents.py\`      — ${agentNodes.length} agents
- \`orchestrator.py\`— ${className(graph.name)}Orchestrator

## Run
1. Implement \`kernel.LlmProvider.complete\` against your provider.
2. \`python orchestrator.py\`
`;

  return [
    { path: 'orchestrator.py', language: 'python', content: orchestrator(graph, usedFns) },
    { path: 'agents.py', language: 'python', content: agentsFile },
    { path: 'guardrails.py', language: 'python', content: guardrailKernel(usedFns) },
    { path: 'kernel.py', language: 'python', content: KERNEL },
    { path: 'requirements.txt', language: 'text', content: requirements },
    { path: 'README.md', language: 'text', content: readme },
  ];
}
